const RELOAD_KEY_PREFIX = 'erp:chunk-reload';
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'chunkloaderror',
  'loading chunk'
];

interface ReloadStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

export const isChunkError = (reason: unknown): boolean => {
  const message =
    reason instanceof Error
      ? `${reason.name} ${reason.message}`
      : typeof reason === 'string'
        ? reason
        : reason && typeof reason === 'object' && 'message' in reason
          ? String((reason as { message?: unknown }).message)
          : '';

  const normalizedMessage = message.toLowerCase();

  return CHUNK_ERROR_PATTERNS.some(pattern => normalizedMessage.includes(pattern));
};

export const getAppVersion = (): string => {
  if (typeof document === 'undefined') return 'unknown';

  const scriptSources = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
    .map(script => script.src)
    .filter(Boolean)
    .sort();

  return scriptSources.join('|') || 'unknown';
};

export const shouldReload = ({ storage, appVersion }: { storage: ReloadStorage; appVersion: string }): boolean => {
  const storageKey = `${RELOAD_KEY_PREFIX}:${appVersion}`;

  if (storage.getItem(storageKey) === '1') return false;

  storage.setItem(storageKey, '1');
  return true;
};

export const reloadOnChunkError = (reason: unknown) => {
  if (!isChunkError(reason)) return;
  if (typeof window === 'undefined') return;

  try {
    const canReload = shouldReload({
      storage: window.sessionStorage,
      appVersion: getAppVersion()
    });

    if (canReload) {
      window.location.reload();
    }
  } catch {
    window.location.reload();
  }
};

export const setupChunkReload = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', event => {
    reloadOnChunkError(event.error || event.message);
  });

  window.addEventListener('unhandledrejection', event => {
    reloadOnChunkError(event.reason);
  });
};
